import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { useRegisterMutation, useVerifyEmailCodeMutation } from '../store/api/authApi';

interface FormErrors {
  email?: string;
  password?: string;
  firstName?: string;
  agreeToTerms?: string;
}

export function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [register, { isLoading }] = useRegisterMutation();
  const [verifyCode, { isLoading: isVerifying }] = useVerifyEmailCodeMutation();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [localError, setLocalError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [codeVerified, setCodeVerified] = useState(false);
  const [touched, setTouched] = useState({
    email: false,
    password: false,
    firstName: false,
  });
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  const validateEmail = (email: string): string | undefined => {
    if (!email.trim()) {
      return t('validation.email.required');
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return t('validation.email.invalid');
    }
    return undefined;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) {
      return t('validation.password.required');
    }
    if (password.length < 8) {
      return t('validation.password.minLength');
    }
    if (password.length > 128) {
      return t('validation.password.maxLength');
    }
    return undefined;
  };

  const validateFirstName = (firstName: string): string | undefined => {
    if (!firstName.trim()) {
      return t('validation.firstName.required');
    }
    if (firstName.trim().length < 1) {
      return t('validation.firstName.minLength');
    }
    if (firstName.trim().length > 255) {
      return t('validation.firstName.maxLength');
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;
    
    const passwordError = validatePassword(formData.password);
    if (passwordError) newErrors.password = passwordError;
    
    const firstNameError = validateFirstName(formData.firstName);
    if (firstNameError) newErrors.firstName = firstNameError;

    if (!agreeToTerms) {
      newErrors.agreeToTerms = t('auth.register.agreeToTermsError');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setSuccess(false);
    
    setTouched({ email: true, password: true, firstName: true });
    
    if (!validateForm()) {
      return;
    }

    try {
      const result = await register(formData).unwrap();
      
      if (result.status === 'success') {
        setSuccess(true);
        setRegisteredEmail(formData.email);
      }
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      setLocalError(
        error?.data?.message || error?.message || t('errors.network')
      );
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setCodeError(null);

    if (!code.trim() || code.trim().length < 4) {
      setCodeError(t('auth.register.wrongCode'));
      return;
    }

    try {
      await verifyCode({ email: registeredEmail, code: code.trim() }).unwrap();
      setCodeVerified(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      setCodeError(error?.data?.message || t('auth.register.wrongCode'));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name as keyof FormErrors];
        return newErrors;
      });
    }
    
    if (touched[name as keyof typeof touched]) {
      if (name === 'email') {
        const error = validateEmail(value);
        setErrors((prev) => ({
          ...prev,
          email: error,
        }));
      } else if (name === 'password') {
        const error = validatePassword(value);
        setErrors((prev) => ({
          ...prev,
          password: error,
        }));
      } else if (name === 'firstName') {
        const error = validateFirstName(value);
        setErrors((prev) => ({
          ...prev,
          firstName: error,
        }));
      }
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    
    if (name === 'email') {
      const error = validateEmail(value);
      setErrors((prev) => ({
        ...prev,
        email: error,
      }));
    } else if (name === 'password') {
      const error = validatePassword(value);
      setErrors((prev) => ({
        ...prev,
        password: error,
      }));
    } else if (name === 'firstName') {
      const error = validateFirstName(value);
      setErrors((prev) => ({
        ...prev,
        firstName: error,
      }));
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    setCodeError(null);
  };

  if (codeVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 relative">
        <div className="w-full max-w-md animate-scale-in relative z-10">
          <div className="card-glass rounded-3xl shadow-2xl p-8 space-y-6">
            <div className="text-center space-y-2 animate-slide-down">
              <div className="flex justify-center mb-4">
                <svg className="w-16 h-16 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h1 className="text-2xl font-heading font-bold text-green-400">
                {t('auth.verifyEmail.success')}
              </h1>
              <p className="text-neutral-medium font-body">
                {t('auth.verifyEmail.goToLogin')}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative">
      <div className="w-full max-w-md animate-scale-in relative z-10">
        <div className="card-glass rounded-3xl shadow-2xl p-8 space-y-6">
          {/* Header */}
          {!success && (
            <div className="text-center space-y-2 animate-slide-down">
              <h1 className="text-4xl font-heading font-bold text-brand-accent">
                {t('auth.register.title')}
              </h1>
              <p className="text-neutral-medium font-body">
                {t('auth.register.subtitle')}
              </p>
            </div>
          )}

          {/* Code Verification UI (shown after registration) */}
          {success && (
            <div className="space-y-6 animate-slide-down">
              <div className="text-center space-y-2">
                <div className="flex justify-center mb-2">
                  <svg className="w-12 h-12 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-green-300 font-body font-medium">
                  {t('auth.register.success')}
                </p>
                <p className="text-neutral-medium text-sm font-body">
                  {t('auth.register.codeSent')} {registeredEmail}
                </p>
              </div>

              <form onSubmit={(e) => { void handleVerifyCode(e); }} noValidate className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm font-body font-medium text-neutral-light text-center">
                    {t('auth.register.enterCode')}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={handleCodeChange}
                    className={`w-full px-4 py-3 rounded-xl border bg-brand-primary/50 text-neutral-light text-center text-2xl tracking-[0.5em] placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent transition-all duration-300 font-body ${
                      codeError ? 'border-danger focus:ring-danger' : 'border-neutral-dark'
                    }`}
                    placeholder={t('auth.register.codePlaceholder')}
                    maxLength={6}
                  />
                  {codeError && (
                    <p className="text-danger text-xs font-body mt-1 text-center animate-slide-down">
                      {codeError}
                    </p>
                  )}
                </div>

              <div className="animate-slide-up" style={{ animationDelay: '0.35s' }}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreeToTerms}
                    onChange={(e) => {
                      setAgreeToTerms(e.target.checked);
                      if (errors.agreeToTerms) {
                        setErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.agreeToTerms;
                          return newErrors;
                        });
                      }
                    }}
                    className="mt-1 h-4 w-4 rounded border-neutral-dark bg-brand-primary/50 text-brand-accent focus:ring-brand-accent focus:ring-2"
                  />
                  <span className="text-sm font-body text-neutral-medium">
                    {t('auth.register.agreeToTerms')}{' '}
                    <Link
                      to="/terms"
                      target="_blank"
                      className="text-brand-accent hover:text-brand-accent/80 transition-colors duration-200 font-medium underline"
                    >
                      {t('auth.register.viewTerms')}
                    </Link>
                  </span>
                </label>
                {errors.agreeToTerms && (
                  <p className="text-danger text-xs font-body mt-1 animate-slide-down">
                    {errors.agreeToTerms}
                  </p>
                )}
              </div>

              <button
                  type="submit"
                  disabled={isVerifying || code.length < 4}
                  className="w-full py-3 px-6 bg-brand-accent text-brand-primary font-heading font-semibold rounded-xl shadow-lg hover:shadow-xl hover:shadow-brand-accent/50 transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isVerifying ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-brand-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t('auth.register.verifying')}
                    </span>
                  ) : (
                    t('auth.register.verifyCode')
                  )}
                </button>
              </form>

              <div className="text-center">
                <p className="text-neutral-medium text-xs font-body mb-2">
                  {t('auth.register.didntReceiveEmail')}
                </p>
                <Link
                  to="/verify-email"
                  className="text-sm text-brand-accent hover:text-brand-accent/80 transition-colors duration-200 font-body font-medium"
                >
                  {t('auth.register.resendVerification')}
                </Link>
              </div>
            </div>
          )}

          {/* Error Message */}
          {(localError) && !success && (
            <div className="p-4 bg-danger/20 border border-danger rounded-lg animate-slide-down">
              <p className="text-red-300 text-sm font-body">
                {localError}
              </p>
            </div>
          )}

          {/* Register Form (hidden after successful registration) */}
          {!success && (
            <form onSubmit={(e) => { void handleSubmit(e); }} noValidate className="space-y-5">
              <div className="space-y-2 animate-slide-up" style={{ animationDelay: '0.1s' }}>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-body font-medium text-neutral-light"
                >
                  {t('auth.register.firstName')}
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  autoComplete="given-name"
                  value={formData.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`w-full px-4 py-3 rounded-xl border bg-brand-primary/50 text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent transition-all duration-300 font-body ${
                    errors.firstName && touched.firstName
                      ? 'border-danger focus:ring-danger focus:border-danger'
                      : 'border-neutral-dark focus:border-brand-accent'
                  }`}
                  placeholder={t('auth.register.firstNamePlaceholder')}
                />
                {errors.firstName && touched.firstName && (
                  <p className="text-danger text-xs font-body mt-1 animate-slide-down">
                    {errors.firstName}
                  </p>
                )}
              </div>

              <div className="space-y-2 animate-slide-up" style={{ animationDelay: '0.2s' }}>
                <label
                  htmlFor="email"
                  className="block text-sm font-body font-medium text-neutral-light"
                >
                  {t('auth.login.email')}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`w-full px-4 py-3 rounded-xl border bg-brand-primary/50 text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent transition-all duration-300 font-body ${
                    errors.email && touched.email
                      ? 'border-danger focus:ring-danger focus:border-danger'
                      : 'border-neutral-dark focus:border-brand-accent'
                  }`}
                  placeholder="user@example.com"
                />
                {errors.email && touched.email && (
                  <p className="text-danger text-xs font-body mt-1 animate-slide-down">
                    {errors.email}
                  </p>
                )}
              </div>

              <div className="space-y-2 animate-slide-up" style={{ animationDelay: '0.3s' }}>
                <label
                  htmlFor="password"
                  className="block text-sm font-body font-medium text-neutral-light"
                >
                  {t('auth.login.password')}
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`w-full px-4 py-3 rounded-xl border bg-brand-primary/50 text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent transition-all duration-300 font-body ${
                    errors.password && touched.password
                      ? 'border-danger focus:ring-danger focus:border-danger'
                      : 'border-neutral-dark focus:border-brand-accent'
                  }`}
                  placeholder="••••••••"
                />
                {errors.password && touched.password && (
                  <p className="text-danger text-xs font-body mt-1 animate-slide-down">
                    {errors.password}
                  </p>
                )}
                <p className="text-neutral-medium text-xs font-body">
                  {t('validation.password.hint')}
                </p>
              </div>

              <div className="animate-slide-up" style={{ animationDelay: '0.35s' }}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreeToTerms}
                    onChange={(e) => {
                      setAgreeToTerms(e.target.checked);
                      if (errors.agreeToTerms) {
                        setErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.agreeToTerms;
                          return newErrors;
                        });
                      }
                    }}
                    className="mt-1 h-4 w-4 rounded border-neutral-dark bg-brand-primary/50 text-brand-accent focus:ring-brand-accent focus:ring-2"
                  />
                  <span className="text-sm font-body text-neutral-medium">
                    {t('auth.register.agreeToTerms')}{' '}
                    <Link
                      to="/terms"
                      target="_blank"
                      className="text-brand-accent hover:text-brand-accent/80 transition-colors duration-200 font-medium underline"
                    >
                      {t('auth.register.viewTerms')}
                    </Link>
                  </span>
                </label>
                {errors.agreeToTerms && (
                  <p className="text-danger text-xs font-body mt-1 animate-slide-down">
                    {errors.agreeToTerms}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-6 bg-brand-accent text-brand-primary font-heading font-semibold rounded-xl shadow-lg hover:shadow-xl hover:shadow-brand-accent/50 transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none animate-slide-up"
                style={{ animationDelay: '0.4s' }}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-brand-primary"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    {t('common.loading')}
                  </span>
                ) : (
                  t('auth.register.submit')
                )}
              </button>
            </form>
          )}

          {/* Footer */}
          {!success && (
            <div className="text-center space-y-2 animate-slide-up" style={{ animationDelay: '0.5s' }}>
              <p className="text-neutral-medium text-sm font-body">
                {t('auth.register.alreadyHaveAccount')}{' '}
                <Link
                  to="/login"
                  className="text-brand-accent hover:text-brand-accent/80 transition-colors duration-200 font-medium"
                >
                  {t('auth.register.loginLink')}
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
